import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppLinks - THE ONE RULE FOR WHICH PART OF A MESSAGE IS A LINK (2026-10-01, "live reactions, edits, links").
// Read by BOTH halves: the service writes the archive page's bubbles with `Html` (`DossierHtml.Linked`), and the app
// builds the same anchors node by node with `AppLinkNodes.Fill` for a live bubble, a just-sent bubble, an edited
// one and a translation patch - so a URL is clickable the moment the message shows, not 15 minutes later.
//
// ⛔ HABITAT-FREE: no DOM globals, no `window` - the Bridge imports it (the page writer and the contract test).
//
// ⭐ THE RULE, ON THE RAW TEXT:
//   - a link starts at `http://`, `https://` or `www.` (any letter case) and runs to the first white space,
//     `<`, `>` or `"`;
//   - the sentence around it is taken back off its end: `. , ; : ! ? ) ] }` and `&` `'` are never the last
//     character of a link ("see https://x.com/a." does not link the full stop);
//   - what is left must be more than the prefix itself ("www." alone is a word, not a link);
//   - a bare `www.` host gets `https://` in front of its address (a relative address would resolve against the
//     folder the page sits in).
// ⛔ ONLY http AND https, BY CONSTRUCTION: nothing else can start a link, so `javascript:`, `data:`, `file:` and
//    every other scheme stay text. The text around a link is text - escaped by the page writer, a text node in
//    the app. A hostile message can choose WHERE a link starts; it can never choose a scheme or write markup.
// ⭐ A NEW TAB, ALWAYS (owner ruling 2026-09-12 - following a link in the same tab abandons the conversation), with
//    `rel="noopener noreferrer"` so the opened site cannot reach `window.opener`.

/** One run of a message: plain text (`Href` ""), or a link (`Text` is what is shown, `Href` where it goes). */
export class AppLinkPart {
    public Text: string = "";
    public Href: string = "";
}

export class AppLinks {
    public static readonly Target: string = "_blank";
    public static readonly Rel: string = "noopener noreferrer";
    /** Where a link can start, and how far it runs. A SOURCE STRING: each call makes its own expression (no shared `lastIndex`). */
    private static readonly PatternSource: string = "(https?:\\/\\/|www\\.)[^\\s<>\"]+";
    /** Characters a sentence ends with that a link does not. */
    private static readonly Tail: string = ".,;:!?)]}&'";

    /** Could this text hold a link at all? DOCUMENTED EXCEPTION: two substring tests. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static Maybe = (Text: string): boolean => {
        const Low: string = nz(Text).toLowerCase();
        return Low.indexOf("http") >= 0 || Low.indexOf("www.") >= 0;
    };

    /** The message as runs of text and links, in order. A message with no link is ONE text run. */
    public static Parts = (Text: string): AppLinkPart[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const All: string = nz(Text);
            const Out: AppLinkPart[] = [];
            if (All.length === 0) { return Out; }
            if (AppLinks.Maybe(All) === false) { Out.push(AppLinks.TextRun(All)); return Out; }
            const Pattern: RegExp = new RegExp(AppLinks.PatternSource, "gi");
            let From: number = 0;
            let Hit: RegExpExecArray | null = Pattern.exec(All);
            while (Hit !== null) {
                const Url: string = AppLinks.Trimmed(Hit[0], Hit[1]);
                if (Url.length > 0) {
                    if (Hit.index > From) { Out.push(AppLinks.TextRun(All.substring(From, Hit.index))); }
                    Out.push(AppLinks.LinkRun(Url));
                    From = Hit.index + Url.length;
                }
                Hit = Pattern.exec(All);
            }
            if (From < All.length) { Out.push(AppLinks.TextRun(All.substring(From))); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLinks.Parts");
            return [AppLinks.TextRun(nz(Text))];
        }
    };

    /**
     * The link inside a match with the sentence taken back off its end, or "" when nothing but the prefix is left.
     * DOCUMENTED EXCEPTION: pure string arithmetic over two values in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    private static Trimmed = (Match: string, Prefix: string): string => {
        let Url: string = Match;
        while (Url.length > 0 && AppLinks.Tail.indexOf(Url.charAt(Url.length - 1)) >= 0) { Url = Url.substring(0, Url.length - 1); }
        if (Url.length <= Prefix.length) { return ""; }
        return Url;
    };

    /** DOCUMENTED EXCEPTION: builds a value. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static TextRun = (Text: string): AppLinkPart => {
        const Out: AppLinkPart = new AppLinkPart();
        Out.Text = Text;
        return Out;
    };

    /**
     * A link run: shown as written; a bare `www.` host is addressed over https.
     * DOCUMENTED EXCEPTION: builds a value. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    private static LinkRun = (Url: string): AppLinkPart => {
        const Out: AppLinkPart = new AppLinkPart();
        Out.Text = Url;
        Out.Href = Url;
        if (Url.toLowerCase().indexOf("www.") === 0) { Out.Href = "https://" + Url; }
        return Out;
    };

    /**
     * ⛔ THE LAST LINE OF DEFENCE, ASKED BY BOTH WRITERS: an address is used only when it IS http or https.
     * `Parts` cannot make anything else; this is what a future change to `Parts` has to get past as well.
     * DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: false IS the answer for anything else.
     */
    public static Safe = (Href: string): boolean => {
        return /^https?:\/\/[^\s<>"]+$/i.test(nz(Href));
    };

    /**
     * The message as page markup: every run escaped with the caller's escaper, a link as
     * `<a href target="_blank" rel="noopener noreferrer">`. The page writer's half (`DossierHtml.Linked`).
     */
    public static Html = (Text: string, Escape: (Value: string) => string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (const One of AppLinks.Parts(Text)) {
                if (One.Href.length === 0 || AppLinks.Safe(One.Href) === false) { Out.push(Escape(One.Text)); continue; }
                Out.push("<a href=\"" + Escape(One.Href) + "\" target=\"" + AppLinks.Target + "\" rel=\"" + AppLinks.Rel + "\">"
                    + Escape(One.Text) + "</a>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLinks.Html");
            return Escape(nz(Text));
        }
    };
}

/** What `AppLinkNodes` needs of a node - a real `Element` fits by shape, so does a test's fake. */
export interface ILinkNode {
    textContent: string | null;
    setAttribute(Name: string, Value: string): void;
    appendChild(Child: ILinkNode): unknown;
}

/** What it needs of a document. `createTextNode` is optional: a fake without it gets the words as plain text. */
export interface ILinkDoc {
    createElement(Tag: string): ILinkNode;
    createTextNode?(Text: string): ILinkNode;
}

/**
 * The app's half: a bubble's words with their links, BUILT (never parsed) - text nodes and `<a>` elements whose
 * label is `textContent`. ⛔ No `innerHTML` anywhere; a message's markup stays the characters it was typed as.
 */
export class AppLinkNodes {
    /**
     * Put `Text` into `Node`, replacing what it held. A message with no link (the common case) is exactly
     * `textContent = Text`, as before links existed.
     */
    public static Fill = (Doc: ILinkDoc, Node: ILinkNode, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Runs: AppLinkPart[] = AppLinks.Parts(Text);
            Node.textContent = "";
            if (AppLinkNodes.Linked(Runs) === false || typeof Doc.createTextNode !== "function") { Node.textContent = nz(Text); return; }
            for (const One of Runs) {
                if (One.Href.length === 0 || AppLinks.Safe(One.Href) === false) {
                    Node.appendChild((Doc.createTextNode as (Text: string) => ILinkNode)(One.Text));
                    continue;
                }
                Node.appendChild(AppLinkNodes.Anchor(Doc, One));
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLinkNodes.Fill");
        }
    };

    /** Is any run a link? DOCUMENTED EXCEPTION: a scan of values in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static Linked = (Runs: AppLinkPart[]): boolean => {
        for (const One of Runs) { if (One.Href.length > 0) { return true; } }
        return false;
    };

    /** `<a href target rel>` with its label as text. */
    private static Anchor = (Doc: ILinkDoc, Run: AppLinkPart): ILinkNode => {
        const Out: ILinkNode = Doc.createElement("a");
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.setAttribute("href", Run.Href);
            Out.setAttribute("target", AppLinks.Target);
            Out.setAttribute("rel", AppLinks.Rel);
            Out.textContent = Run.Text;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLinkNodes.Anchor");
            return Out;
        }
    };
}
